import React, { forwardRef } from 'react';
import { OpenInNewRounded } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { declareVariants } from '../../lib/variantMatch';
import { isInternalUrl } from '../Link/Link';
import { TopBarLocationState } from '../TopBar/TopBarProvider';
import { cn } from '../../lib/cn';

type LegacyVariant = 'contained' | 'outlined' | 'text';

type CanonicalVariant = 'filled' | 'tonal' | 'outlined' | 'ghost';

function normalizeButtonVariant(
	v: ButtonProps['variant'] | LegacyVariant | undefined,
): CanonicalVariant {
	if (v === 'contained') return 'filled';
	if (v === 'text') return 'ghost';
	return (v ?? 'filled') as CanonicalVariant;
}

export type ButtonProps = {
	color?: 'primary' | 'secondary';
	variant?: 'filled' | 'tonal' | 'outlined' | 'ghost';
	size?: 'l' | 'm' | 's' | 'xs';
	href?: string;
	hrefState?: Record<string, unknown> & TopBarLocationState;
	icon?: React.ReactNode | true;
	dangerous?: boolean;
	active?: boolean;
	external?: boolean;
	onClick?: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

type RefType = HTMLButtonElement | HTMLAnchorElement;

export const Button = forwardRef<RefType, ButtonProps>(({
	variant = 'filled',
	size = 'm',
	href,
	hrefState,
	icon,
	active,
	dangerous,
	external,
	onClick,
	className,
	children,
	...rest
}, ref) => {
	const history = useHistory();
	const isIconButton = Boolean(icon && !children);

	const variants = declareVariants({
		variant: normalizeButtonVariant(variant),
		size,
		isIconButton,
		dangerous,
		active,
	});

	const commonProps: React.HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> = {
		className: cn(
			!isIconButton && [
				'flex shrink-0 select-none items-center justify-center gap-2 rounded-[4px] px-[20px] text-sm font-semibold leading-5 tracking-[0.14px] box-border transition-colors whitespace-nowrap',
				'[&_svg]:fill-current [&_svg]:size-5!',
				variants({
					variant: {
						filled:
							'bg-component-button-brand-bg-filled_default text-component-button-brand-text-filled hover:bg-component-button-brand-bg-filled_hover active:border-component-button-brand-bg-filled_pressed active:bg-component-button-brand-bg-filled_pressed disabled:bg-component-button-brand-bg-filled_disabled disabled:text-component-button-brand-text-disabled',
						tonal:
							'bg-brand-50 text-component-button-brand-text-brand_default hover:bg-brand-100 active:border-brand-200 active:bg-brand-200 disabled:bg-grey-50 disabled:text-component-button-brand-text-disabled',
						outlined:
							'border bg-cta-bg-outlined text-component-button-brand-text-brand_default active:bg-component-button-brand-bg-outlined_pressed border-component-button-brand-border-outline_default hover:border-component-button-brand-border-outline_hover focus:border-component-button-brand-border-outline_pressed active:border-component-button-brand-border-outline_pressed disabled:border-component-button-brand-border-outlined_disabled disabled:text-component-button-brand-text-disabled',
						ghost:
							'bg-component-button-brand-bg-transparent text-component-button-brand-text-brand_default hover:bg-component-button-brand-bg-ghost_hover hover:text-component-button-brand-text-brand_hover disabled:text-component-button-brand-text-disabled',
					},
					size: {
						l: 'h-[44px]',
						m: 'h-[40px]',
						s: 'h-[36px]',
						xs: 'h-[24px]',
					},
					when: [
						[
							[
								{ variant: 'filled', dangerous: true },
								{ variant: 'tonal', dangerous: true },
							],
							'bg-cherry-400! text-white! hover:bg-cherry-500! focus:bg-cherry-500! active:border-cherry-500! active:bg-cherry-300! active:text-white! disabled:bg-cherry-100! disabled:text-cherry-300!',
						],
						[
							[
								{ variant: 'outlined', dangerous: true },
								{ variant: 'ghost', dangerous: true },
							],
							'text-cherry-400! hover:text-cherry-500! focus:text-cherry-500! active:text-cherry-500! border-cherry-400! disabled:border-cherry-100! disabled:text-cherry-300!',
						],
						[{ variant: 'ghost', size: 'xs' }, 'px-0! w-fit!'],
					],
				}),
			],
			isIconButton && [
				'inline-flex border border-transparent rounded-full transition-all items-center justify-center cursor-pointer',
				'text-grey-650 hover:text-grey-800 hover:bg-cherry-50 active:text-cherry-500 active:border-cherry-500 disabled:text-grey-400',
				'[&_svg]:fill-current [&_svg]:size-5!',
				variants({
					size: {
						l: 'w-[40px] h-[40px]',
						m: 'w-[36px] h-[36px]',
						s: 'w-[24px] h-[24px]',
						xs: 'w-[24px] h-[24px]',
					},
					active: 'border-cherry-500! text-cherry-500!',
				}),
			],
			className,
		),
		onClick: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
			onClick?.(e);
			if (e.defaultPrevented) {
				return;
			}
			if (href && isInternalUrl(href)) {
				e.preventDefault();
				history.push(href, hrefState);
			}
		},
	};

	if (href) {
		return (
			// eslint-disable-next-line react/jsx-no-target-blank
			<a
				ref={ref as React.Ref<HTMLAnchorElement>}
				href={href}
				target={isInternalUrl(href) ? '_self' : '_blank'}
				rel={isInternalUrl(href) ? undefined : 'noopener noreferrer'}
				{...commonProps}
				{...rest}
			>
				{icon}
				{children}
				{(href && (external === true || (!isInternalUrl(href) && external !== false))) && <OpenInNewRounded className="size-5" />}
			</a>
		);
	} else {
		return (
			<button
				ref={ref as React.Ref<HTMLButtonElement>}
				type="button"
				{...commonProps}
				{...rest}
			>
				{icon}
				{children}
			</button>
		);
	}
});
